import { useEffect, useState } from "react";

import { fetchOrder } from "../api/fetchOrder";
import type { ICaaOrder } from "../interface/customer-account";
import type { ILineItem, IReturnLineItem } from "../interface/order";
import type { IUseOrderDetailCaaProps } from "../types/hooks";
import { extractId } from "../utils/common";
import { sortOrderItemsCaa, toCaaOrderLineItem } from "../utils/order";
import useCaaLineProducts from "./useCaaLineProducts";

const STORE_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

/**
 * classic useOrderDetail의 CAA 대응 — 구조(state + getOrder)와 반환 형태를 맞춤
 *
 * 반품 진행중(요청/승인)이거나 부분환불/부분결제 상태이거나, 삭제된 상품(variant 없음)이 있으면
 * classic과 동일하게 fetchOrder(admin API)로 반품 배송비를 포함한 classic 모양의 line item을 받아 그대로 사용
 *
 * classic과 다른 점
 * - fallback 실패 시 주문내역으로 보내지 않고 CAA 데이터로 계속 보여줌
 * - classic의 orderNotFound → admin 조회 경로는 없음 (CAA는 order(id:) 단건 조회가 가능)
 */
export default function useOrderDetailCaa({ lang, order, customerId, openToast }: IUseOrderDetailCaaProps) {
  const { fetchLineProducts } = useCaaLineProducts({ lang, openToast });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  /* 주문 진행 내역을 관리하는 state - (unfulfilled, fulfilled, onhold) */
  const [completeOrder, setCompleteOrder] = useState<(ILineItem | IReturnLineItem)[]>([]);
  /* 반품 내역을 관리하는 state - (returnRequested, returnApproved) */
  const [returnOrder, setReturnOrder] = useState<IReturnLineItem[]>([]);
  /* 환불 내역을 관리하는 state - (canceled, refunded) */
  const [refundOrder, setRefundOrder] = useState<(ILineItem | IReturnLineItem)[]>([]);
  /* 반품 배송비를 관리하는 state */
  const [totalReturnShippingFee, setTotalReturnShippingFee] = useState<number>(0);

  useEffect(() => {
    // 새로고침 직후엔 caaCustomer 복구(App)보다 단건 주문 조회가 먼저 끝날 수 있다 — customerId가 채워진 뒤 조회
    if (!order || !customerId) return;

    setCompleteOrder([]);
    setReturnOrder([]);
    setRefundOrder([]);
    setTotalReturnShippingFee(0);

    const returnStatus = order.returnStatus?.value;
    const isReturnOrPartiallyRefunded =
      order.financialStatus !== "REFUNDED" &&
      (returnStatus === "requested" ||
        returnStatus === "approved" ||
        order.financialStatus === "PARTIALLY_REFUNDED" ||
        order.financialStatus === "PARTIALLY_PAID");

    // 아카이브된 상품이거나 삭제된 상품의 경우 어드민 API를 호출
    const hasDeletedProduct = order.lineItems.edges.some((edge) => !edge.node.variantId);

    if (isReturnOrPartiallyRefunded || hasDeletedProduct) {
      void getOrder(order, customerId);
    } else {
      void setOrderFromCaa(order);
    }
  }, [lang, order?.id, order?.financialStatus, order?.returnStatus?.value, customerId]);

  // classic의 else 분기와 동일 — 전체 환불이면 전부 환불 내역, 아니면 전부 주문 진행 내역
  // (getOrder 실패 시 대체로도 쓰여 부분환불 주문이 들어올 수 있지만, 그 경우도 classic처럼 나누지 않는다)
  const setOrderFromCaa = async (caaOrder: ICaaOrder) => {
    setIsLoading(true);

    // 상품 tags/notice/compareAtPrice를 채우기 위해 현재 상품 조회 (useCaaLineProducts)
    const products = await fetchLineProducts(caaOrder);
    const orderLineItems = caaOrder.lineItems.edges.map(({ node }) =>
      toCaaOrderLineItem(node, node.productId ? products.get(node.productId) : null),
    );

    if (caaOrder.financialStatus === "REFUNDED") {
      // 취소 or 전체 환불을 어드민에서 처리해줄 경우
      setRefundOrder(orderLineItems);
    } else {
      setCompleteOrder(orderLineItems);
    }

    setIsLoading(false);
  };

  // 어드민 API로 classic 모양(ILineItem/IReturnLineItem)의 주문 조회 — 실패하면 CAA 데이터로 대신 보여줌
  const getOrder = async (caaOrder: ICaaOrder, caaCustomerId: string) => {
    setIsLoading(true);

    try {
      const response = await fetchOrder({
        storeName: STORE_NAME,
        domain: ({ ko: "", en: "intl", jp: "jp" } as const)[lang] ?? "",
        orderId: extractId(caaOrder.id),
        customerId: extractId(caaCustomerId),
      });

      if (!response) {
        await setOrderFromCaa(caaOrder);
        return;
      }

      setTotalReturnShippingFee(response.totalReturnShippingFee);
      setCompleteOrder(response.lineItems);
      setReturnOrder(response.returnLineItems);
      setRefundOrder(response.refundLineItems);
    } catch {
      await setOrderFromCaa(caaOrder);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    completeOrder: sortOrderItemsCaa(completeOrder),
    returnOrder: sortOrderItemsCaa(returnOrder),
    refundOrder: sortOrderItemsCaa(refundOrder),
    totalReturnShippingFee,
    isLoading,
  };
}
